/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageCircle, Trash2, EyeOff, Eye, Shield, BarChart3, Filter,
  Heart, MessageSquare, Clock, AlertTriangle, Send, ImageIcon, X, Award,
  Download, Loader2
} from 'lucide-react';
import JSZip from 'jszip';
import { Button } from '../../components/ui/button';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sanitizeFilename = (value) =>
  (value || 'member')
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'member';

const guessExtensionFromUrl = (url, blob) => {
  const mime = blob?.type || '';
  if (mime.includes('png')) return 'png';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('gif')) return 'gif';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\.([a-zA-Z0-9]{2,5})(?:$|\?)/);
    if (match) return match[1].toLowerCase();
  } catch { /* ignore */ }
  return 'jpg';
};

const triggerBlobDownload = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
};

const OFFICIAL_LABELS = ['DCCF Board', 'DCCF Team', 'Official Chamber Announcement', 'Membership Team', 'Events Team'];

const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString();
};

const CommunityModerationPage = () => {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showComposer, setShowComposer] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [officialLabel, setOfficialLabel] = useState('DCCF Board');
  const [posting, setPosting] = useState(false);
  const [downloadingPostId, setDownloadingPostId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/community`, { withCredentials: true }),
        axios.get(`${API}/admin/community/stats`, { withCredentials: true }),
      ]);
      setPosts(postsRes.data);
      setStats(statsRes.data);
    } catch { /* */ } finally { setLoading(false); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/community/${postId}`, { withCredentials: true });
      toast.success('Post deleted');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleHide = async (postId) => {
    try {
      const res = await axios.put(`${API}/admin/community/${postId}/hide`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleOfficialPost = async () => {
    if (!newPost.trim()) { toast.error('Write something'); return; }
    setPosting(true);
    try {
      await axios.post(`${API}/portal/community`, {
        content: newPost,
        post_type: 'official',
        is_official: true,
        official_label: officialLabel,
        images: [],
      }, { withCredentials: true });
      toast.success('Official post published!');
      setNewPost('');
      setShowComposer(false);
      fetchData();
    } catch (err) { toast.error('Failed to post'); } finally { setPosting(false); }
  };

  // Download helpers --------------------------------------------------------
  const fetchImageBlob = async (rawSrc) => {
    const url = resolveImg(rawSrc);
    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return { blob, url };
  };

  const handleDownloadPostImages = async (post, index) => {
    const images = post.images || [];
    if (images.length === 0) { toast.error('No hay imágenes en este post'); return; }
    setDownloadingPostId(post.id);
    try {
      const authorSlug = sanitizeFilename(post.author?.name || post.official_label || 'member');
      const stamp = post.created_at
        ? new Date(post.created_at).toISOString().slice(0, 10)
        : 'post';

      if (images.length === 1) {
        const { blob, url } = await fetchImageBlob(images[0]);
        const ext = guessExtensionFromUrl(url, blob);
        triggerBlobDownload(blob, `${authorSlug}_${stamp}_1.${ext}`);
      } else {
        const zip = new JSZip();
        const folder = zip.folder(`${authorSlug}_${stamp}`);
        let i = 0;
        for (const src of images) {
          i += 1;
          try {
            const { blob, url } = await fetchImageBlob(src);
            const ext = guessExtensionFromUrl(url, blob);
            folder.file(`${authorSlug}_${stamp}_${i}.${ext}`, blob);
          } catch (err) {
            // keep going on individual failures
          }
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        triggerBlobDownload(zipBlob, `${authorSlug}_${stamp}.zip`);
      }
      toast.success(`Descargadas ${images.length} imagen(es)`);
    } catch (err) {
      toast.error('No se pudieron descargar las imágenes');
    } finally {
      setDownloadingPostId(null);
    }
  };

  const handleDownloadAllMemberImages = async () => {
    const memberPostsWithImages = posts.filter(p => !p.is_official && (p.images?.length || 0) > 0);
    if (memberPostsWithImages.length === 0) {
      toast.error('No hay imágenes de miembros para descargar');
      return;
    }
    setDownloadingAll(true);
    const toastId = toast.loading(`Preparando ZIP de ${memberPostsWithImages.length} post(s)...`);
    try {
      const zip = new JSZip();
      let totalImages = 0;
      let failed = 0;
      for (const post of memberPostsWithImages) {
        const authorSlug = sanitizeFilename(post.author?.name || 'member');
        const stamp = post.created_at
          ? new Date(post.created_at).toISOString().slice(0, 10)
          : 'post';
        const postIdShort = (post.id || '').toString().slice(0, 8);
        const folder = zip.folder(`${authorSlug}_${stamp}_${postIdShort}`);
        let i = 0;
        for (const src of post.images || []) {
          i += 1;
          try {
            const { blob, url } = await fetchImageBlob(src);
            const ext = guessExtensionFromUrl(url, blob);
            folder.file(`${authorSlug}_${stamp}_${i}.${ext}`, blob);
            totalImages += 1;
          } catch (err) {
            failed += 1;
          }
        }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const stampNow = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(zipBlob, `dccf_community_member_images_${stampNow}.zip`);
      toast.success(
        `Descargadas ${totalImages} imágenes${failed ? ` (${failed} con error)` : ''}`,
        { id: toastId }
      );
    } catch (err) {
      toast.error('No se pudo generar el ZIP', { id: toastId });
    } finally {
      setDownloadingAll(false);
    }
  };

  const filtered = filter === 'all' ? posts :
    filter === 'official' ? posts.filter(p => p.is_official) :
    filter === 'hidden' ? posts.filter(p => p.is_hidden) :
    posts;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-community-moderation">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#002249]">Community Moderation</h1>
          <p className="text-slate-600 text-sm mt-1">Monitor activity, moderate posts, and publish official communications.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleDownloadAllMemberImages}
            disabled={downloadingAll}
            variant="outline"
            className="rounded-sm border-slate-300 text-[#002249] hover:bg-slate-50"
            data-testid="download-all-member-images-btn"
            title="Descargar todas las imágenes de posts de miembros en un ZIP"
          >
            {downloadingAll
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {downloadingAll ? 'Preparando...' : 'Descargar imágenes'}
          </Button>
          <Button onClick={() => setShowComposer(!showComposer)} className="bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="official-post-btn">
            <Award className="w-4 h-4 mr-2" />Post as Chamber
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Posts', value: stats.total_posts, color: 'bg-blue-500' },
            { label: 'Posts Today', value: stats.posts_today, color: 'bg-green-500' },
            { label: 'Official Posts', value: stats.official_posts, color: 'bg-purple-500' },
            { label: 'Hidden Posts', value: stats.hidden_posts, color: 'bg-orange-500' },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`stat-${i}`}>
              <div className={`w-8 h-8 ${s.color} rounded-sm flex items-center justify-center mb-2`}>
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <p className="text-2xl font-bold text-[#002249]">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Official Post Composer */}
      {showComposer && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="bg-white border-2 border-[#002249] rounded-sm mb-6 overflow-hidden" data-testid="official-composer">
          <div className="p-4 bg-[#002249]/5 border-b border-[#002249]/10 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Award className="w-5 h-5 text-[#002249]" />
              <h3 className="font-bold text-[#002249] text-sm">Publish as Official Chamber Voice</h3>
            </div>
            <button onClick={() => setShowComposer(false)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Publishing as:</label>
              <select value={officialLabel} onChange={e => setOfficialLabel(e.target.value)} className="border border-slate-200 rounded-sm p-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#002249]/20" data-testid="official-label-select">
                {OFFICIAL_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <textarea
              value={newPost}
              onChange={e => setNewPost(e.target.value)}
              placeholder="Write an official chamber message..."
              className="w-full border border-slate-200 rounded-sm p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
              data-testid="official-post-content"
            />
            <div className="flex justify-end">
              <Button onClick={handleOfficialPost} disabled={posting} className="bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="publish-official-btn">
                <Send className="w-4 h-4 mr-2" />{posting ? 'Publishing...' : 'Publish Official Post'}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <div className="flex items-center space-x-2 mb-4">
        <Filter className="w-4 h-4 text-slate-500" />
        {['all', 'official', 'hidden'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${filter === f ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`} data-testid={`filter-${f}`}>
            {f === 'all' ? `All (${posts.length})` : f === 'official' ? `Official (${posts.filter(p => p.is_official).length})` : `Hidden (${posts.filter(p => p.is_hidden).length})`}
          </button>
        ))}
      </div>

      {/* Posts */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" /><p>No posts found</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map((post, index) => (
            <div key={post.id} className={`bg-white border rounded-sm ${post.is_hidden ? 'border-orange-200 bg-orange-50/30 opacity-60' : post.is_official ? 'border-[#002249]/30 bg-blue-50/20' : 'border-slate-200'}`} data-testid={`mod-post-${index}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${post.is_official ? 'bg-[#002249]' : 'bg-gradient-to-br from-[#E63946] to-[#F4A261]'}`}>
                      {post.is_official ? 'D' : post.author?.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-1.5">
                        <span className="font-semibold text-sm text-[#050505]">{post.is_official ? (post.official_label || 'DCCF') : (post.author?.name || 'Unknown')}</span>
                        {post.is_official && <span className="text-[10px] font-bold bg-[#002249] text-white px-1.5 py-0.5 rounded">OFFICIAL</span>}
                        {post.is_hidden && <span className="text-[10px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded">HIDDEN</span>}
                        <span className="text-xs text-slate-400">&middot; {getTimeAgo(post.created_at)}</span>
                      </div>
                      {!post.is_official && (
                        <p className="text-xs text-slate-500">{post.author?.email} &middot; {post.author?.role} {post.company?.name ? `&middot; ${post.company.name}` : ''}</p>
                      )}
                      <p className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{post.content}</p>
                      {post.images?.length > 0 && (
                        <div className="flex gap-1 mt-2">{post.images.map((img, i) => <Img variant="generic" key={i} src={resolveImg(img)} alt="" className="w-16 h-16 object-cover rounded border" />)}</div>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center"><Heart className="w-3 h-3 mr-1" />{post.likes_count || 0}</span>
                        <span className="flex items-center"><MessageSquare className="w-3 h-3 mr-1" />{post.comments_count || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    {(post.images?.length || 0) > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadPostImages(post, index)}
                        disabled={downloadingPostId === post.id}
                        title={`Descargar ${post.images.length} imagen(es)`}
                        data-testid={`download-post-images-${index}`}
                      >
                        {downloadingPostId === post.id
                          ? <Loader2 className="w-4 h-4 text-[#002249] animate-spin" />
                          : <Download className="w-4 h-4 text-[#002249]" />}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleHide(post.id)} title={post.is_hidden ? 'Unhide' : 'Hide'} data-testid={`hide-post-${index}`}>
                      {post.is_hidden ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-orange-500" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(post.id)} title="Delete" data-testid={`delete-mod-post-${index}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunityModerationPage;
